import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Popup from '../../components/Popup';
import '../../styles/myroom.css';
import Loading from '../../components/Loading';

function MyRoom() {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [roomsPerPage] = useState(6);
  const navigate = useNavigate();
  const [popup, setPopup] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'info',
    onConfirm: null
  });

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    const fetchRooms = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/my-rooms`,
          { 
            headers: { Authorization: `Bearer ${token}` },
            timeout: 5000
          }
        );
        
        const formattedRooms = response.data.map(room => ({
          _id: room._id,
          roomId: room.roomId,
          name: room.name,
          participantsCount: room.participantsCount || 0,
          createdAt: room.createdAt,
          status: room.isCompleted ? 'Đã hoàn thành' : 'Đang diễn ra',
          hasPassword: !!room.password,
          products: room.products || [],
          productWinners: room.productWinners || {},
          isCompleted: room.isCompleted,
          isLocked: room.isLocked,
          maxParticipants: room.maxParticipants
        }));
        
        setRooms(formattedRooms);
      } catch (error) {
        console.error('Error fetching rooms:', error);
        if (error.response?.status === 401) {
          localStorage.removeItem('token');
          navigate('/login');
        } else {
          setPopup({
            isOpen: true,
            title: 'Lỗi',
            message: 'Không thể tải phòng. Vui lòng thử lại sau.',
            type: 'error',
            showCancelButton: false,
            onConfirm: () => setPopup({ isOpen: false })
          });
        }
      } finally {
        setLoading(false);
      }
    };

    fetchRooms();
  }, [navigate]);

  const handleDeleteRoom = async (roomId) => {
    setPopup({
      isOpen: true,
      title: 'Xác nhận xóa',
      message: 'Bạn có chắc chắn muốn xóa phòng này không?',
      type: 'warning',
      showCancelButton: true,
      onConfirm: async () => {
        try {
          const token = localStorage.getItem('token');
          await axios.delete(
            `${process.env.REACT_APP_API_URL}/my-rooms/${roomId}`,
            {
              headers: { Authorization: `Bearer ${token}` }
            }
          );

          setRooms(prevRooms => prevRooms.filter(room => room.roomId !== roomId));
          
          setPopup({
            isOpen: true,
            title: 'Thành công',
            message: 'Đã xóa phòng thành công',
            type: 'success',
            showCancelButton: false,
            onConfirm: () => setPopup({ isOpen: false })
          });

        } catch (error) {
          console.error('Delete room error:', error);
          setPopup({
            isOpen: true,
            title: 'Lỗi',
            message: 'Không thể xóa phòng. Vui lòng thử lại sau.',
            type: 'error',
            showCancelButton: false,
            onConfirm: () => setPopup({ isOpen: false })
          });
        }
      },
      onCancel: () => setPopup({ isOpen: false })
    });
  };

  // Lọc phòng theo tìm kiếm
  const filteredRooms = rooms.filter(room =>
    room.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Phân trang
  const indexOfLastRoom = currentPage * roomsPerPage;
  const indexOfFirstRoom = indexOfLastRoom - roomsPerPage;
  const currentRooms = filteredRooms.slice(indexOfFirstRoom, indexOfLastRoom);

  const renderRoomCard = (room) => (
    <div key={room._id} className="room-card">
      <div className="room-card-header">
        <div>
          <h3>{room.name}</h3>
          <span className="room-id">ID: {room.roomId}</span>
        </div>
        {room.isLocked && (
          <i className="fas fa-lock text-gray-400" title="Phòng đã khóa"></i>
        )}
      </div>
      
      <div className="room-info-container">
        <div className="room-info-item" title="Số người tham gia">
          <i className="fas fa-users"></i>
          <span>{room.participantsCount}/{room.maxParticipants}</span>
        </div>

        <div className="room-info-item">
          <span className={`myroom-status ${room.isCompleted ? 'ended' : 'active'}`}>
            <i className={`fas fa-${room.isCompleted ? 'check-circle' : 'clock'}`}></i>
            {room.isCompleted ? 'Đã hoàn thành' : 'Đang diễn ra'}
          </span>
        </div>

        <div className="room-info-item">
          <i className="fas fa-calendar"></i>
          <span>{new Date(room.createdAt).toLocaleDateString('vi-VN')}</span>
        </div>
      </div>

      <div className="room-actions">
        <Link 
          to={`/room/${room.roomId}`}
          className="btn btn-primary"
        >
          <i className="fas fa-door-open"></i>
          Vào phòng
        </Link>
        <button
          onClick={() => handleDeleteRoom(room.roomId)}
          className="btn btn-outline-danger"
        >
          <i className="fas fa-trash-alt"></i>
          Xóa
        </button>
      </div>
    </div>
  );

  if (loading) return <Loading />;

  return (
    <div className="myroom-container">
      <div className="myroom-header">
        <h1>Phòng của tôi</h1>
        <Link to="/create-room" className="btn btn-create">
          <i className="fas fa-plus-circle me-2"></i>
          Tạo phòng mới
        </Link>
      </div>

      <div className="search-box mb-4">
        <input
          type="text"
          className="form-control"
          placeholder="Tìm kiếm phòng..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {currentRooms.length === 0 ? (
        <div className="no-rooms">
          <i className="fas fa-folder-open mb-3"></i>
          <p>{searchTerm ? 'Không tìm thấy phòng phù hợp' : 'Bạn chưa tạo phòng nào'}</p>
          {!searchTerm && (
            <Link to="/create-room" className="btn btn-primary">
              Tạo phòng đầu tiên
            </Link>
          )}
        </div>
      ) : (
        <div className="rooms-grid">
          {currentRooms.map(renderRoomCard)}
        </div>
      )}

      {filteredRooms.length > roomsPerPage && (
        <div className="pagination">
          {Array.from({ length: Math.ceil(filteredRooms.length / roomsPerPage) })
            .map((_, index) => (
              <button
                key={index}
                className={`pagination-btn ${currentPage === index + 1 ? 'active' : ''}`}
                onClick={() => setCurrentPage(index + 1)}
              >
                {index + 1}
              </button>
            ))}
        </div>
      )}

      <Popup
        isOpen={popup.isOpen}
        title={popup.title}
        message={popup.message}
        type={popup.type}
        onConfirm={popup.onConfirm}
        onCancel={popup.onCancel}
        showCancelButton={popup.showCancelButton}
      />
    </div>
  );
}

export default MyRoom; 